// Owns the in-flight confirm-modal state for material delete +
// restore (single + bulk). Extracted from the inventory orchestrator
// so `ManufacturingInventoryPanel.tsx` stays under the file-size cap
// after picking up the dev-sync 184bc79f delete-with-restore slice.
//
// Contract:
//   - The orchestrator passes in cleanup callbacks (single + bulk
//     deactivate / restore) and a `onSummaryToast` callback that
//     emits a one-line summary string.
//   - Row + toolbar surfaces call the `request*` functions; this
//     hook stores the pending request as `pending` so the modal can
//     render. While the modal is busy the same `pending` carries the
//     `busy: true` flag so the cancel button greys out.
//   - On `confirm()`, the hook fans out to the right cleanup callback
//     based on the pending kind, awaits the result, surfaces the
//     toast, and clears the pending state. Errors stay in `pending`
//     so the modal can show them inline before the user retries or
//     cancels.
//   - `cancel()` clears the pending state immediately. The hook
//     ignores cancel() while `busy` is true so a misclick during a
//     bulk write doesn't leave the system in a half-applied state
//     with no UI feedback.

import { useCallback, useState } from "react";
import type { MfgMaterial } from "@/lib/manufacturing/types";

type AsyncResult = { ok: true } | { ok: false; error: string };
type BulkSummary = { updated: number; failed: number };

type SinglePending =
  | { kind: "single-delete"; material: MfgMaterial; busy: boolean; error: string | null }
  | { kind: "single-restore"; material: MfgMaterial; busy: boolean; error: string | null };

type BulkPending =
  | {
      kind: "bulk-delete";
      materialIds: ReadonlyArray<string>;
      busy: boolean;
      error: string | null;
    }
  | {
      kind: "bulk-restore";
      materialIds: ReadonlyArray<string>;
      busy: boolean;
      error: string | null;
    };

export type MaterialDeletionPending = SinglePending | BulkPending;

type Deps = {
  deactivateMaterial: (material: MfgMaterial) => Promise<AsyncResult>;
  restoreMaterial: (material: MfgMaterial) => Promise<AsyncResult>;
  bulkDeactivateMaterials: (ids: ReadonlyArray<string>) => Promise<BulkSummary>;
  bulkRestoreMaterials: (ids: ReadonlyArray<string>) => Promise<BulkSummary>;
  /** Called with a one-line summary string after a successful op so
   *  the orchestrator can render its existing toast surface. */
  onSummaryToast: (message: string) => void;
  /** Called after a successful bulk op so the orchestrator can clear
   *  selection + refresh the materials list. */
  onBulkApplied?: () => void;
};

async function runSingle(
  snapshot: SinglePending,
  deps: Deps,
  setPending: (next: MaterialDeletionPending | null) => void
) {
  const action =
    snapshot.kind === "single-delete" ? deps.deactivateMaterial : deps.restoreMaterial;
  const verb = snapshot.kind === "single-delete" ? "Deleted" : "Restored";
  const r = await action(snapshot.material);
  if (!r.ok) {
    setPending({ ...snapshot, busy: false, error: r.error });
    return;
  }
  deps.onSummaryToast(`${verb} ${snapshot.material.name}`);
  setPending(null);
}

async function runBulk(
  snapshot: BulkPending,
  deps: Deps,
  setPending: (next: MaterialDeletionPending | null) => void
) {
  const bulkAction =
    snapshot.kind === "bulk-delete"
      ? deps.bulkDeactivateMaterials
      : deps.bulkRestoreMaterials;
  const verb = snapshot.kind === "bulk-delete" ? "Deleted" : "Restored";
  const summary = await bulkAction(snapshot.materialIds);
  deps.onBulkApplied?.();
  const parts: string[] = [`${verb} ${summary.updated}`];
  if (summary.failed > 0) parts.push(`${summary.failed} failed`);
  deps.onSummaryToast(parts.join(" · "));
  setPending(null);
}

export function useMaterialDeletionFlow(deps: Deps) {
  const [pending, setPending] = useState<MaterialDeletionPending | null>(null);

  const requestSingleDelete = useCallback((material: MfgMaterial) => {
    setPending({ kind: "single-delete", material, busy: false, error: null });
  }, []);

  const requestSingleRestore = useCallback((material: MfgMaterial) => {
    setPending({ kind: "single-restore", material, busy: false, error: null });
  }, []);

  const requestBulkDelete = useCallback((materialIds: ReadonlyArray<string>) => {
    if (materialIds.length === 0) return;
    setPending({ kind: "bulk-delete", materialIds, busy: false, error: null });
  }, []);

  const requestBulkRestore = useCallback((materialIds: ReadonlyArray<string>) => {
    if (materialIds.length === 0) return;
    setPending({ kind: "bulk-restore", materialIds, busy: false, error: null });
  }, []);

  const cancel = useCallback(() => {
    setPending((prev) => (prev?.busy ? prev : null));
  }, []);

  const confirm = useCallback(async () => {
    setPending((prev) => (prev ? { ...prev, busy: true, error: null } : prev));
    const snapshot = pending;
    if (!snapshot) return;

    if (snapshot.kind === "single-delete" || snapshot.kind === "single-restore") {
      await runSingle(snapshot, deps, setPending);
      return;
    }
    await runBulk(snapshot, deps, setPending);
  }, [pending, deps]);

  return {
    pending,
    requestSingleDelete,
    requestSingleRestore,
    requestBulkDelete,
    requestBulkRestore,
    confirm,
    cancel,
  };
}
